// trackLinks — the SAME-DOMAIN link to a library track, for family mail.
//
// Why this exists (2026-10-05). The Oct 1 audition-cut email to a Yahoo
// address went to spam. Same sender, same path, same template as two earlier
// emails to the same parent that arrived; the only difference was four links
// to firebasestorage.googleapis.com, a host spam filters score badly because
// phishing campaigns live on it. So a family email never names the Storage
// host. It links coloryourvoice.com/t/<storage path>?k=<token>, a public page
// (src/pages/TrackLink) that plays the file and offers the download; the
// Storage URL exists only inside that page.
//
// The link is stateless on purpose: the path and the download token are the
// same two facts the Storage URL carries, so the page needs no Firestore read
// and no rule change, and a wrong token fails at Storage exactly as before.
// Only paths under tracks/ are accepted, so the route cannot be used to frame
// anything else in the bucket.

import { trackLabel } from './studentTracksModel';

export const STORAGE_HOST = 'https://firebasestorage.googleapis.com';
export const DEFAULT_BUCKET = 'color-your-voice.appspot.com';
export const SITE_ORIGIN = 'https://coloryourvoice.com';
export const TRACK_LINK_PREFIX = '/t/';

const TOKEN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A playable-copy path: under tracks/, a real file name, no traversal. */
export const isTrackPath = (path) => {
	const p = typeof path === 'string' ? path : '';
	if (!p.startsWith('tracks/')) return false;
	const segs = p.split('/');
	if (segs.length < 3) return false;
	return segs.every((s) => s !== '' && s !== '.' && s !== '..');
};

export const isDownloadToken = (token) => TOKEN_RE.test(String(token || ''));

/** Pull {bucket, path, token} out of a Storage download URL, or null. */
export const parseStorageUrl = (url) => {
	let u;
	try {
		u = new URL(String(url || ''));
	} catch (e) {
		return null;
	}
	if (u.origin !== STORAGE_HOST) return null;
	const m = u.pathname.match(/^\/v0\/b\/([^/]+)\/o\/(.+)$/);
	if (!m) return null;
	let path;
	try {
		path = decodeURIComponent(m[2]);
	} catch (e) {
		return null;
	}
	const token = u.searchParams.get('token') || '';
	return { bucket: m[1], path, token };
};

/** The download URL the page plays from: the same shape functions/index.js
 *  saveWithDownloadUrl writes and playbackUrlFor accepts. */
export const storageUrlFor = ({ path, token, bucket = DEFAULT_BUCKET }) =>
	`${STORAGE_HOST}/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media&token=${token}`;

const encodePath = (path) => path.split('/').map(encodeURIComponent).join('/');

/**
 * The link that goes in an email. Accepts a studentTracks file ({storagePath,
 * storageUrl}) or a bare Storage URL string. Returns null when the file has no
 * Storage copy or the token is missing: a caller must then link the Songs
 * page, never fall back to a Drive link (systems.md, Music library).
 */
export const trackLinkFor = (fileOrUrl, origin = SITE_ORIGIN) => {
	const parsed = parseStorageUrl(typeof fileOrUrl === 'string' ? fileOrUrl : fileOrUrl && fileOrUrl.storageUrl);
	if (!parsed) return null;
	const path = (fileOrUrl && typeof fileOrUrl === 'object' && fileOrUrl.storagePath) || parsed.path;
	if (!isTrackPath(path) || !isDownloadToken(parsed.token)) return null;
	return `${origin}${TRACK_LINK_PREFIX}${encodePath(path)}?k=${parsed.token}`;
};

/**
 * What the page reads back from its own URL. `splat` is React Router's `*`
 * param (already decoded), `search` the query string. Null when either half
 * is unusable, which the page renders as "This link isn't valid."
 */
export const parseTrackLink = (splat, search) => {
	const path = typeof splat === 'string' ? splat : '';
	let token = '';
	try {
		token = new URLSearchParams(search || '').get('k') || '';
	} catch (e) {
		token = '';
	}
	if (!isTrackPath(path) || !isDownloadToken(token)) return null;
	return { path, token };
};

/**
 * The family-facing words for a file, from the library naming convention
 * `<Title> (<Show or Artist>) - <Length> - <Variant> [- <Key>].ext`
 * (systems.md, Music library, Naming). Anything that does not parse shows
 * its bare file name rather than nothing.
 */
export const describeTrackFile = (path) => {
	const file = String(path || '').split('/').pop() || '';
	const stem = file.replace(/\.[A-Za-z0-9]+$/, '');
	const parts = stem.split(' - ').map((s) => s.trim());
	if (parts.length < 3) return { title: stem, detail: '' };
	const [title, length, variant, key] = parts;
	const label = trackLabel({ variant, key: key || null });
	return { title, detail: length ? `${label} · ${length}` : label };
};
